/**
 * Browser speech-to-text helper for the vishing call: lets the learner SPEAK the
 * verification code instead of (or as well as) typing it on the keypad. Uses the
 * Web Speech API (SpeechRecognition) where available; returns null otherwise so
 * the keypad remains the fallback. We only parse digits, so the scripted caller
 * (whose lines never contain digits) can't trigger a false "code heard".
 */

export interface CodeListener {
  stop: () => void;
}

type SRType = new () => SRInstance;
interface SRInstance {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((e: SRResultEvent) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
}
interface SRResultEvent {
  resultIndex: number;
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
}

const NUM_WORDS: Record<string, string> = {
  zero: "0", oh: "0", o: "0", nought: "0",
  one: "1", two: "2", to: "2", too: "2",
  three: "3", four: "4", for: "4", five: "5",
  six: "6", seven: "7", eight: "8", ate: "8", nine: "9",
  // light multilingual coverage (de/fr) for spoken digits
  null: "0", eins: "1", zwei: "2", drei: "3", vier: "4", "fünf": "5", funf: "5",
  sechs: "6", sieben: "7", acht: "8", neun: "9",
  un: "1", deux: "2", trois: "3", quatre: "4", cinq: "5", sept: "7", huit: "8", neuf: "9",
};

/** Pull a digit string out of free speech ("eight oh four two" -> "8042"). */
export function speechToDigits(s: string): string {
  const tokens = s.toLowerCase().split(/[^a-z0-9äöü]+/i).filter(Boolean);
  let out = "";
  for (const t of tokens) {
    if (/^\d+$/.test(t)) out += t;
    else if (NUM_WORDS[t] != null) out += NUM_WORDS[t];
  }
  return out;
}

export function listenForCode(opts: {
  lang: string;
  minLen: number;
  onDigits: (digits: string) => void;
  onText?: (text: string, final: boolean) => void;
}): CodeListener | null {
  const w = window as unknown as { SpeechRecognition?: SRType; webkitSpeechRecognition?: SRType };
  const SR = w.SpeechRecognition || w.webkitSpeechRecognition;
  if (!SR) return null;

  let rec: SRInstance | null = new SR();
  let stopped = false;
  let fired = false;

  rec.lang = opts.lang;
  rec.continuous = true;
  rec.interimResults = true;

  rec.onresult = (e: SRResultEvent) => {
    let text = "";
    for (let i = e.resultIndex; i < e.results.length; i++) {
      text += e.results[i][0].transcript;
    }
    const last = e.results[e.results.length - 1];
    opts.onText?.(text.trim(), !!last?.isFinal);
    const digits = speechToDigits(text);
    if (!fired && digits.length >= opts.minLen) {
      fired = true;
      opts.onDigits(digits.slice(0, opts.minLen));
    }
  };
  rec.onerror = () => {};
  rec.onend = () => {
    // Chrome stops recognition periodically; keep listening until we stop it.
    if (!stopped && rec) {
      try {
        rec.start();
      } catch {
        /* ignore */
      }
    }
  };

  try {
    rec.start();
  } catch {
    return null;
  }

  return {
    stop: () => {
      stopped = true;
      try {
        rec?.stop();
      } catch {
        /* ignore */
      }
      rec = null;
    },
  };
}
